/**
 * PositionTable — renders the main positions table with sort headers
 * and expandable rows. Receives positions data + callbacks from Positions.js.
 */
import PositionExpandedRow from './PositionExpandedRow';
import { formatOptionSymbol, formatLocalTime, formatDuration } from '../../utils/helpers';

const SortTh = ({ column, label, sort, onSort, style = {} }) => (
  <th
    style={{
      padding: '0.75rem',
      textAlign: 'left',
      fontWeight: '600',
      color: '#cbd5e1',
      ...style,
    }}
  >
    <button
      onClick={() => onSort(column)}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontWeight: '600',
        color: '#cbd5e1',
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: 0,
      }}
    >
      {label}{' '}
      {sort.orderBy === column && (sort.order === 'ASC' ? '↑' : '↓')}
    </button>
  </th>
);

const PlainTh = ({ label, style = {} }) => (
  <th
    style={{
      padding: '0.75rem',
      textAlign: 'left',
      fontWeight: '600',
      color: '#cbd5e1',
      ...style,
    }}
  >
    {label}
  </th>
);

const pnlColor = (val) => (val >= 0 ? '#059669' : '#dc2626');

/**
 * Derive display symbol for options positions.
 * Uses position-level optionType/strikePrice/expirationDate if available,
 * otherwise falls back to formatOptionSymbol on the raw symbol.
 */
const getDisplaySymbol = (position) => {
  if (position.securityType !== 'OPTION') return position.symbol;

  // If position has parsed option fields, build readable string
  if (position.optionType && position.strikePrice) {
    const underlying = position.underlyingSymbol || position.symbol;
    const type = position.optionType === 'CALL' ? 'C' : position.optionType === 'PUT' ? 'P' : position.optionType;
    const strike = position.strikePrice % 1 === 0 ? position.strikePrice.toFixed(0) : position.strikePrice.toFixed(1);
    let expiry = '';
    if (position.expirationDate) {
      const d = new Date(position.expirationDate);
      expiry = ` ${d.getMonth() + 1}/${d.getDate()}`;
    }
    return `${underlying} ${strike}${type}${expiry}`;
  }

  // Fallback: try OCC symbol parsing
  return formatOptionSymbol(position.symbol);
};

/**
 * Compute last exit time from trades array (SELL trades' entryTime).
 * More reliable than the stored lastExitTime which may have sorting issues.
 */
const getLastExitTime = (position) => {
  if (!position.trades || position.trades.length === 0) return position.lastExitTime;
  const sellTrades = position.trades.filter(t =>
    t.side === 'SELL' || t.side === 'SELL_TO_CLOSE' || t.side === 'SELL_TO_OPEN' || t.side === 'SELL_SHORT'
  );
  if (sellTrades.length === 0) return null;
  const times = sellTrades.map(t => new Date(t.entryTime || t.exitTime || 0).getTime());
  const latest = new Date(Math.max(...times));
  return latest.toISOString();
};

/**
 * Compute first entry time from trades array (BUY trades' entryTime).
 */
const getFirstEntryTime = (position) => {
  if (!position.trades || position.trades.length === 0) return position.firstEntryTime;
  const buyTrades = position.trades.filter(t =>
    t.side === 'BUY' || t.side === 'BUY_TO_OPEN' || t.side === 'BUY_TO_COVER' || t.side === 'BUY_TO_CLOSE'
  );
  if (buyTrades.length === 0) return position.firstEntryTime;
  const times = buyTrades.map(t => new Date(t.entryTime || t.exitTime || 0).getTime());
  const earliest = new Date(Math.min(...times));
  return earliest.toISOString();
};

const PositionTable = ({ positions, sort, onSort, expandedPositions, onToggleExpand, onOpenChart }) => {
  if (positions.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: '48px', marginBottom: '1rem' }}>📊</div>
        <p style={{ color: '#6b7280', fontSize: '1.125rem' }}>
          No positions yet. Create positions from your trades to see aggregated P&amp;L!
        </p>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ backgroundColor: '#1e293b', borderBottom: '2px solid #334155' }}>
            <SortTh column="symbol" label="Symbol" sort={sort} onSort={onSort} />
            <PlainTh label="Type" />
            <PlainTh label="Qty" />
            <SortTh column="average_entry_price" label="Avg Entry" sort={sort} onSort={onSort} />
            <SortTh column="unrealized_pnl" label="Unrealized P&L" sort={sort} onSort={onSort} />
            <SortTh column="realized_pnl" label="Realized P&L" sort={sort} onSort={onSort} />
            <SortTh column="total_pnl" label="Total P&L" sort={sort} onSort={onSort} />
            <PlainTh label="ROI%" />
            <PlainTh label="Status" />
            <PlainTh label="Duration" />
            <SortTh column="first_entry_time" label="Opened" sort={sort} onSort={onSort} />
            <PlainTh label="Closed" />
            <SortTh column="trade_count" label="Trades" sort={sort} onSort={onSort} />
          </tr>
        </thead>
        <tbody>
          {positions.map((position) => {
            const isExpanded = expandedPositions.has(position.id);
            const hasMultipleTrades = position.trades?.length > 1;
            const displaySymbol = getDisplaySymbol(position);

            // Compute from trades for accuracy
            const firstEntry = getFirstEntryTime(position);
            const lastExit = getLastExitTime(position);
            const isClosed = position.status === 'closed';

            const roi = position.totalEntryCost
              ? ((position.totalPnl || 0) / Math.abs(position.totalEntryCost) * 100)
              : 0;

            return (
              <>
                <tr
                  key={position.id}
                  style={{
                    borderBottom: '1px solid #334155',
                    cursor: hasMultipleTrades ? 'pointer' : 'default',
                    backgroundColor: isExpanded ? '#1e293b' : undefined,
                  }}
                  onClick={() => hasMultipleTrades && onToggleExpand(position.id)}
                >
                  {/* Symbol */}
                  <td style={{ padding: '0.75rem', fontWeight: '500' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {hasMultipleTrades && (
                        <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 'bold', minWidth: '12px' }}>
                          {isExpanded ? '−' : '+'}
                        </span>
                      )}
                      <div>
                        <span
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: '#3b82f6' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenChart(position.symbol, position.underlyingSymbol, position.trades);
                          }}
                        >
                          {displaySymbol}
                        </span>
                        {position.securityType === 'OPTION' && position.underlyingSymbol && position.underlyingSymbol !== displaySymbol && (
                          <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{position.underlyingSymbol}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  <td style={{ padding: '0.75rem' }}>{position.securityType === 'OPTION' ? 'Option' : 'Stock'}</td>
                  <td style={{ padding: '0.75rem' }}>{position.netQuantity}</td>
                  <td style={{ padding: '0.75rem' }}>${position.averageEntryPrice?.toFixed(2) || 'N/A'}</td>

                  {/* Unrealized P&L */}
                  <td style={{ padding: '0.75rem', fontWeight: '500', color: pnlColor(position.unrealizedPnl) }}>
                    {position.unrealizedPnl ? `$${position.unrealizedPnl.toFixed(2)}` : 'N/A'}
                  </td>

                  {/* Realized P&L */}
                  <td style={{ padding: '0.75rem', fontWeight: '500', color: pnlColor(position.realizedPnl) }}>
                    ${position.realizedPnl?.toFixed(2) || '0.00'}
                  </td>

                  {/* Total P&L */}
                  <td style={{ padding: '0.75rem', fontWeight: '500', color: pnlColor(position.totalPnl) }}>
                    ${position.totalPnl?.toFixed(2) || '0.00'}
                  </td>

                  {/* ROI% */}
                  <td style={{ padding: '0.75rem', fontWeight: '500', color: pnlColor(roi) }}>
                    {roi.toFixed(1)}%
                  </td>

                  <td style={{ padding: '0.75rem' }}>
                    {position.status
                      ? position.status.charAt(0).toUpperCase() + position.status.slice(1)
                      : 'Unknown'}
                  </td>

                  {/* Duration — computed from trades */}
                  <td style={{ padding: '0.75rem', color: '#94a3b8' }}>
                    {formatDuration(firstEntry, isClosed ? lastExit : null)}
                  </td>

                  {/* Opened */}
                  <td style={{ padding: '0.75rem' }}>
                    {formatLocalTime(firstEntry)}
                  </td>

                  {/* Closed */}
                  <td style={{ padding: '0.75rem' }}>
                    {isClosed && lastExit ? formatLocalTime(lastExit) : isClosed ? 'N/A' : 'Open'}
                  </td>

                  {/* Trade count badge */}
                  <td style={{ padding: '0.75rem' }}>
                    <span
                      style={{
                        backgroundColor: hasMultipleTrades ? '#dbeafe' : '#f3f4f6',
                        color: hasMultipleTrades ? '#1e40af' : '#374151',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                      }}
                    >
                      {position.trades?.length || 0} trade{position.trades?.length !== 1 ? 's' : ''}
                    </span>
                  </td>
                </tr>

                {/* Expanded row */}
                {isExpanded && position.trades && <PositionExpandedRow position={position} />}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PositionTable;
